"use client";

import { ArrowUpRight } from "lucide-react";
import Image from "next/image";

import { useOpenCity } from "../../_context/OpenCityContext";
import { flightParts } from "../../_logic/flight-format";
import type { RouteFlight } from "../../_logic/types";
import { safeUrl } from "../../_logic/saved/fares-schema";

interface FlightSummaryProps {
  flight: RouteFlight;
  /** Route shown when the airline is unknown, e.g. "LHR–LIS". */
  fallbackRoute: string | null;
  destination: string;
  /** The cabin this group flies; the line only names the airline's cabin when it differs. */
  tripCabin: string;
  /**
   * `inline`: one left-aligned line for a row with room. `stacked`: right-aligned under the price,
   * for a city column or card, showing as much as the column's width allows.
   */
  layout: "inline" | "stacked";
}

const LINK = "rounded-sm outline-none transition-colors focus-visible:underline";

/**
 * The flight behind a fare on one line that gives way as its column narrows: the airline logo and
 * "Direct" or "1 stop" always, then the flight number ("UA 2491"), then the duration, then the
 * times. The cabin only shows when it differs from the group's. The whole line is in the tooltip,
 * and "View" (plus "N other options" where it fits) opens the details.
 * @param props - The flight, a fallback route label, the destination, the group's cabin and the layout.
 * @returns The summary.
 * @throws Never.
 */
export const FlightSummary = ({ flight, fallbackRoute, destination, tripCabin, layout }: FlightSummaryProps) => {
  // Hooks
  const openCity = useOpenCity();

  // Derived
  const parts = flightParts(flight, tripCabin);
  const inline = layout === "inline";
  const dot = <span aria-hidden>·</span>;
  const logoUrl = safeUrl(flight.airlineLogo);
  const url = flight.url ? safeUrl(flight.url) : undefined;
  const Line = url ? "a" : "span";
  const logo = logoUrl ? (
    <Image src={logoUrl} alt="" width={16} height={16} unoptimized className="size-4 shrink-0 object-contain" />
  ) : null;
  const others = flight.otherOptions
    ? `${flight.otherOptions} other ${flight.otherOptions === 1 ? "option" : "options"}`
    : null;

  // Render
  return (
    <span
      className={`flex w-full min-w-0 flex-col gap-1 text-xs font-medium text-gray-500 @container ${inline ? "items-start text-left" : "items-end text-right"}`}
    >
      {/* The flight line is the link to book it; the arrow shows on the row's hover or focus (always on
          touch), so the table doesn't repeat "View" in every cell. */}
      <Line
        title={parts.full || fallbackRoute || undefined}
        className={`flex min-w-0 max-w-full items-center gap-1.5 overflow-hidden whitespace-nowrap ${inline ? "" : "justify-end"} ${url ? `text-gray-600 hover:text-indigo-700 ${LINK}` : ""}`}
        {...(url
          ? {
              href: url,
              target: "_blank",
              rel: "noopener noreferrer",
              "aria-label": `View this flight${parts.full ? `: ${parts.full}` : ""}`,
            }
          : {})}
      >
        <span className="sr-only">{parts.full || fallbackRoute}</span>
        {logo}
        {inline && parts.airline && (
          <span aria-hidden className="hidden shrink-0 text-sm text-gray-800 @[420px]:inline">
            {parts.airline}
          </span>
        )}
        {parts.number ? (
          <span aria-hidden className={`shrink-0 text-gray-800 ${inline ? "text-sm" : "hidden @[150px]:inline"}`}>
            {parts.number}
          </span>
        ) : (
          !parts.airline && fallbackRoute && <span aria-hidden>{fallbackRoute}</span>
        )}
        <span
          aria-hidden
          className={inline ? "hidden @[180px]:inline" : parts.number ? "hidden @[150px]:inline" : "hidden"}
        >
          ·
        </span>
        {/* Shown whole or not at all, so a narrow cell never cuts "Direct" mid-word. */}
        <span aria-hidden className={`shrink-0 ${inline ? "hidden @[180px]:inline" : ""}`}>
          {parts.stops}
        </span>
        {/* The connecting airport shows whole or not at all, never cut to "via K". */}
        {parts.via && (
          <span
            aria-hidden
            className={`hidden shrink-0 whitespace-nowrap ${inline ? "@[260px]:inline" : "@[240px]:inline"}`}
          >
            via {parts.via}
          </span>
        )}
        {parts.duration && (
          // Inline rows spell out "1 stop via PHL", so the duration needs more room before it fits.
          <span
            aria-hidden
            className={`hidden shrink-0 items-center gap-1.5 ${inline ? "@[320px]:inline-flex" : "@[210px]:inline-flex"}`}
          >
            {dot}
            {parts.duration}
          </span>
        )}
        {parts.times && (
          <span
            aria-hidden
            className={`hidden shrink-0 items-center gap-1.5 ${inline ? "@[440px]:inline-flex" : "@[290px]:inline-flex"}`}
          >
            {dot}
            {parts.times}
          </span>
        )}
        {parts.cabin && (
          <span aria-hidden title={parts.cabin} className="hidden truncate @[200px]:inline">
            · {parts.cabin}
          </span>
        )}
        {url && (
          <ArrowUpRight
            aria-hidden
            className="size-3 shrink-0 text-indigo-600 opacity-0 transition-opacity group-focus-within/row:opacity-100 group-hover/row:opacity-100 [@media(hover:none)]:opacity-100"
          />
        )}
      </Line>
      {others && (
        <span className="hidden items-center gap-1.5 whitespace-nowrap @[200px]:inline-flex">
          {/* ui-lint-disable-next-line raw-control: a custom control (segment, chip, calendar day, stepper, icon or inline editor) styled to its row; FetchButton's sizes and 3D treatment don't fit */}
          <button
            type="button"
            onClick={(event) => {
              event.currentTarget.blur();
              openCity(destination);
            }}
            className={`text-gray-600 hover:text-gray-900 ${LINK}`}
          >
            {others}
          </button>
        </span>
      )}
    </span>
  );
};
